import { useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import {
  DAILY_LIFE_COPY,
  DEVELOPMENT_COPY,
  EMPTY_STATES,
  GROWTH_REFERENCE_EDITION,
  REFERENCE_PERCENTILES,
  coveredMonths,
  monthsBetween,
  normalizeSex,
  parseCalendarDate,
  percentileOf,
  referenceCurve,
  type GrowthMetric,
  type GrowthSex,
} from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import { Card, Chip, EmptyState, ErrorNote, Spinner } from '@/components/ui';
import { apiData } from '@/lib/api';
import { formatDate } from '@/lib/format';
import type { ChildSummary, GrowthMeasurement } from '@/types';

const METRICS: { key: GrowthMetric; label: string; unit: string }[] = [
  { key: 'heightCm', label: '키', unit: 'cm' },
  { key: 'weightKg', label: '몸무게', unit: 'kg' },
  { key: 'headCm', label: '머리둘레', unit: 'cm' },
];

interface Point {
  date: string;
  months: number;
  value: number;
}

/** 신체 성장 — 명세서 §5.11 */
export function GrowthPage() {
  const childrenQuery = useQuery({
    queryKey: ['children'],
    queryFn: () => apiData<ChildSummary[]>('/children'),
  });
  const children = childrenQuery.data;

  const [selectedChild, setSelectedChild] = useState('');
  const [metric, setMetric] = useState<GrowthMetric>('heightCm');

  const child = children?.find((c) => c.id === selectedChild) ?? children?.[0];
  const childId = child?.id ?? '';
  const sex = normalizeSex(child?.sex);

  const { data, isPending, isPlaceholderData, isError, refetch } = useQuery({
    queryKey: ['growth', childId],
    queryFn: () => apiData<GrowthMeasurement[]>(`/health/growth?childId=${childId}`),
    enabled: Boolean(childId),
    placeholderData: keepPreviousData, // 아이 전환 시 화면 유지
  });

  /** 계측일의 월령을 함께 계산한다 — 참조선의 x축이 나이이기 때문 */
  const points: Point[] = useMemo(() => {
    if (!child?.birthDate) return [];
    const birth = parseCalendarDate(child.birthDate);
    return (data ?? [])
      .filter((row) => row[metric] != null)
      .map((row) => ({
        date: row.measuredOn,
        months: monthsBetween(birth, parseCalendarDate(row.measuredOn)),
        value: row[metric] as number,
      }));
  }, [data, metric, child?.birthDate]);

  const unit = METRICS.find((m) => m.key === metric)?.unit ?? '';
  // `.at(-1)`은 옛 WebView(Chrome 92 미만)에 없다 — 인덱스 접근으로 둔다
  const latest = points[points.length - 1];
  const latestPercentile =
    sex && latest ? percentileOf(metric, sex, latest.months, latest.value) : null;

  return (
    <Screen>
      <AppBar title="신체 성장" back />

      <div className="flex flex-col gap-4 p-4">
        {(children?.length ?? 0) > 1 && (
          <div className="flex flex-wrap gap-2">
            {children?.map((item) => (
              <Chip
                key={item.id}
                tone={item.id === childId ? 'primary' : 'neutral'}
                selected={item.id === childId}
                onClick={() => setSelectedChild(item.id)}
              >
                {item.name}
              </Chip>
            ))}
          </div>
        )}

        {child && (
          <div className="flex flex-wrap gap-2">
            {METRICS.map((item) => (
              <Chip
                key={item.key}
                tone={item.key === metric ? 'primary' : 'neutral'}
                selected={item.key === metric}
                onClick={() => setMetric(item.key)}
              >
                {item.label}
              </Chip>
            ))}
          </div>
        )}

        {(childrenQuery.isPending || (Boolean(childId) && isPending)) && <Spinner />}
        {childrenQuery.isError && (
          <ErrorNote message="불러오지 못했어요." onRetry={() => void childrenQuery.refetch()} />
        )}
        {isError && <ErrorNote message="불러오지 못했어요." onRetry={() => void refetch()} />}

        {/* 원아가 없으면 성장 쿼리가 비활성인 채 isPending=true로 남는다 —
            여기서 빈 상태를 그리지 않으면 스피너가 끝나지 않는다 */}
        {childrenQuery.isSuccess && !child && (
          <EmptyState title={EMPTY_STATES.children.title} body={EMPTY_STATES.children.body} />
        )}

        {Boolean(childId) && !isPending && !isError && points.length === 0 && (
          <EmptyState title={EMPTY_STATES.growth.title} body={EMPTY_STATES.growth.body} />
        )}

        {points.length > 0 && (
          <>
            <Card>
              {/* 이전 아이 계측 + 새 아이 birthDate가 섞이면 월령이 틀어지므로 차트만 보류 */}
              {isPlaceholderData ? (
                <Spinner />
              ) : (
                <GrowthChart points={points} metric={metric} sex={sex} unit={unit} />
              )}

              <p className="mt-3 text-sm text-ink-sub">{DAILY_LIFE_COPY.health.growthNotice}</p>

              {!sex && (
                <p className="mt-1 text-sm text-ink-sub">{DAILY_LIFE_COPY.health.sexUnknown}</p>
              )}

              {latestPercentile !== null && (
                <p className="mt-2 text-base">
                  최근 계측 — {DAILY_LIFE_COPY.health.percentileLabel(latestPercentile)}
                </p>
              )}

              {/* 어떤 판을 기준으로 그린 선인지 밝힌다 (§5.11) */}
              <p className="mt-3 flex items-start gap-1.5 rounded-sm bg-bg px-3 py-2 text-sm text-ink-sub">
                <Info size={14} className="mt-0.5 shrink-0" />
                <span>
                  {DAILY_LIFE_COPY.health.referenceEdition(
                    `${GROWTH_REFERENCE_EDITION.publisher} ${GROWTH_REFERENCE_EDITION.label}`,
                    GROWTH_REFERENCE_EDITION.nextRevisionYear,
                  )}
                </span>
              </p>
            </Card>

            <Card>
              <p className="mb-2 font-semibold">기록</p>
              <ul className="flex flex-col gap-1.5">
                {[...points].reverse().map((point) => {
                  const percentile = sex ? percentileOf(metric, sex, point.months, point.value) : null;
                  return (
                    <li key={point.date} className="flex items-baseline justify-between text-base">
                      <span className="text-ink-sub">{formatDate(point.date)}</span>
                      <span className="flex items-baseline gap-2">
                        {percentile !== null && (
                          <span className="tnum text-sm text-ink-sub">
                            {Math.round(percentile)}번째
                          </span>
                        )}
                        <span className="tnum font-semibold">
                          {point.value}
                          {unit}
                        </span>
                      </span>
                    </li>
                  );
                })}
              </ul>
            </Card>

            <p className="px-1 text-sm text-ink-sub">{DEVELOPMENT_COPY.everyChildOwnPace}</p>
          </>
        )}
      </div>
    </Screen>
  );
}

/**
 * 성장 곡선 — 질병관리청 백분위 참조선 + 아이의 계측값 (§5.11).
 *
 * 그린 명도 스케일만 쓴다(§7.1 — 기록 화면에 빨강·주황·노랑 금지). 참조선은 연한 회녹색으로
 * 배경처럼 두고 아이의 선만 진하게 그린다. 등급이 아니라 배경이라는 것을 색으로 말한다.
 */
function GrowthChart({
  points,
  metric,
  sex,
  unit,
}: {
  points: Point[];
  metric: GrowthMetric;
  sex: GrowthSex | null;
  unit: string;
}) {
  const width = 340;
  const height = 210;
  const padding = { top: 12, right: 34, bottom: 26, left: 36 };

  const covered = coveredMonths(metric);

  // x축은 나이(개월). 계측 구간에 여유를 두되 참조 자료 범위를 벗어나지 않는다.
  const rawFrom = Math.min(...points.map((p) => p.months));
  const rawTo = Math.max(...points.map((p) => p.months));
  const pad = Math.max(2, (rawTo - rawFrom) * 0.15);
  const fromMonths = Math.max(covered.from, rawFrom - pad);
  const toMonths = Math.min(covered.to, rawTo + pad);

  const curves = sex
    ? REFERENCE_PERCENTILES.map((percentile) => ({
        percentile,
        points: referenceCurve(metric, sex, percentile, fromMonths, toMonths),
      })).filter((curve) => curve.points.length > 1)
    : [];

  // y축은 참조선과 계측값을 모두 담아야 잘리지 않는다.
  const allValues = [...points.map((p) => p.value), ...curves.flatMap((c) => c.points.map((p) => p.value))];
  const min = Math.min(...allValues);
  const max = Math.max(...allValues);
  const span = max - min < 1 ? 2 : max - min;
  const low = min - span * 0.08;
  const high = max + span * 0.08;

  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;
  const monthSpan = Math.max(1, toMonths - fromMonths);

  const x = (months: number) => padding.left + ((months - fromMonths) / monthSpan) * plotWidth;
  const y = (value: number) => padding.top + plotHeight - ((value - low) / (high - low)) * plotHeight;

  const pathOf = (list: { months: number; value: number }[]) =>
    list.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(p.months).toFixed(1)} ${y(p.value).toFixed(1)}`).join(' ');

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="w-full"
      role="img"
      aria-label={`성장 그래프 — 계측 ${points.length}회${sex ? ', 또래 분포 참조선 포함' : ''}`}
    >
      {[0, 0.5, 1].map((ratio) => {
        const value = high - (high - low) * ratio;
        const lineY = padding.top + plotHeight * ratio;
        return (
          <g key={ratio}>
            <line
              x1={padding.left}
              x2={width - padding.right}
              y1={lineY}
              y2={lineY}
              stroke="var(--color-line)"
              strokeWidth={1}
            />
            <text x={2} y={lineY + 4} fontSize={10} fill="var(--color-ink-sub)">
              {value.toFixed(value < 20 ? 1 : 0)}
            </text>
          </g>
        );
      })}

      {/* 또래 분포 참조선 — 배경이므로 연하게, 중앙값만 조금 진하게 */}
      {curves.map((curve) => (
        <g key={curve.percentile}>
          <path
            d={pathOf(curve.points)}
            fill="none"
            stroke="var(--color-primary)"
            strokeWidth={curve.percentile === 50 ? 1.4 : 1}
            strokeDasharray={curve.percentile === 50 ? undefined : '3 3'}
            opacity={curve.percentile === 50 ? 0.42 : 0.26}
          />
          <text
            x={width - padding.right + 3}
            y={y(curve.points[curve.points.length - 1].value) + 3}
            fontSize={9}
            fill="var(--color-ink-sub)"
          >
            {curve.percentile}
          </text>
        </g>
      ))}

      <path
        d={pathOf(points)}
        fill="none"
        stroke="var(--color-primary-strong)"
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {points.map((point) => (
        <circle
          key={point.date}
          cx={x(point.months)}
          cy={y(point.value)}
          r={4}
          fill="var(--color-primary-strong)"
        />
      ))}

      <text x={padding.left} y={height - 6} fontSize={10} fill="var(--color-ink-sub)">
        {monthLabel(fromMonths)}
      </text>
      <text
        x={width - padding.right}
        y={height - 6}
        fontSize={10}
        textAnchor="end"
        fill="var(--color-ink-sub)"
      >
        {monthLabel(toMonths)}
      </text>
      <text
        x={width - padding.right}
        y={padding.top}
        fontSize={10}
        textAnchor="end"
        fill="var(--color-ink-sub)"
      >
        {unit}
      </text>
    </svg>
  );
}

/** x축 라벨 — 개월이 크면 "n세"가 읽기 쉽다 */
function monthLabel(months: number): string {
  const rounded = Math.round(months);
  if (rounded < 24) return `${rounded}개월`;
  const years = Math.floor(rounded / 12);
  const rest = rounded % 12;
  return rest === 0 ? `${years}세` : `${years}세 ${rest}개월`;
}
